/**
 * Generation Job Client Service
 * 
 * Client-side service for checking and resuming async generation jobs
 * (videos, images) that may have been started before a page reload.
 */

import { supabase } from '../lib/supabase';
import logger from '../utils/logger';

export interface GenerationJob {
  id: string;
  flowId: string | null;
  nodeId: string;
  userId: string;
  provider: 'google' | 'openai' | 'anthropic' | 'falai';
  model: string;
  operationId: string;
  jobType: 'video' | 'image' | 'audio';
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  resultUrl: string | null;
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
}

class GenerationJobClientService {
  private apiBase: string;

  constructor() {
    this.apiBase = import.meta.env.VITE_API_URL || '';
  }

  /**
   * Get current user ID
   */
  private async getUserId(): Promise<string | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      return user?.id || null;
    } catch {
      return null;
    }
  }

  /**
   * Get all pending/processing jobs for the current user
   */
  async getPendingJobs(): Promise<GenerationJob[]> {
    const userId = await this.getUserId();
    if (!userId) {
      logger.debug('[GenerationJobClient] No user logged in');
      return [];
    }

    try {
      const response = await fetch(`${this.apiBase}/api/jobs/pending`, {
        headers: {
          'x-user-id': userId
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch pending jobs: ${response.status}`);
      }

      const jobs = await response.json();
      logger.debug('[GenerationJobClient] Fetched pending jobs:', jobs.length);
      return jobs;
    } catch (error) {
      logger.error('[GenerationJobClient] Error fetching pending jobs:', error);
      return [];
    }
  }

  /**
   * Get pending jobs for a specific flow
   */
  async getPendingJobsForFlow(flowId: string): Promise<GenerationJob[]> {
    const result = await this.getJobsForFlow(flowId);
    return result.pending;
  }

  /**
   * Get all jobs (pending and completed) for a specific flow
   * Completed jobs are those that finished while the browser was closed
   */
  async getJobsForFlow(flowId: string): Promise<{ pending: GenerationJob[], completed: GenerationJob[] }> {
    const userId = await this.getUserId();
    if (!userId) {
      return { pending: [], completed: [] };
    }

    try {
      const response = await fetch(`${this.apiBase}/api/jobs/flow/${encodeURIComponent(flowId)}`, {
        headers: {
          'x-user-id': userId
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch flow jobs: ${response.status}`);
      }

      const data = await response.json();
      // Server returns { pending: [...], completed: [...] }
      const pending = data.pending || [];
      const completed = data.completed || [];
      logger.debug('[GenerationJobClient] Fetched jobs for flow:', flowId, { pending: pending.length, completed: completed.length });
      return { pending, completed };
    } catch (error) {
      logger.error('[GenerationJobClient] Error fetching flow jobs:', error);
      return { pending: [], completed: [] };
    }
  }

  /**
   * Get a specific job by operation ID
   */
  async getJobByOperationId(operationId: string): Promise<GenerationJob | null> {
    const userId = await this.getUserId();
    if (!userId) {
      return null;
    }

    try {
      const response = await fetch(`${this.apiBase}/api/jobs/operation/${encodeURIComponent(operationId)}`, {
        headers: {
          'x-user-id': userId
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`Failed to fetch job: ${response.status}`);
      }

      const job = await response.json();
      return job;
    } catch (error) {
      logger.error('[GenerationJobClient] Error fetching job:', error);
      return null;
    }
  }

  /**
   * Poll VEO operation status and update job
   */
  async pollVeoStatus(operationId: string): Promise<{
    done: boolean;
    status: string;
    videoUri?: string;
    /** Server-persisted R2 media-proxy URL (present on current servers). */
    videoUrl?: string;
    error?: string;
  }> {
    const response = await fetch(`${this.apiBase}/api/veo/video/${encodeURIComponent(operationId)}`);
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      return { done: true, status: 'failed', error: error.error };
    }

    return await response.json();
  }

}

// Export singleton instance
const generationJobClientService = new GenerationJobClientService();
export default generationJobClientService;
